import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import { Edit, GripVertical, Trash2, Clock } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { DeleteConfirmModal } from '../shared/DeleteConfirmModal';
import { ListItem } from '@/types/lists';
import { useTemplatesStore } from '@/store/templatesStore';
import { TemplateItemEditForm } from './TemplateItemEditForm';
import { cn } from '@/utils/cn';

type TemplateItem = Omit<ListItem, 'id' | 'createdAt' | 'priority'>;

interface TemplateItemListProps {
  templateId: string;
  items: TemplateItem[];
  allowEditing?: boolean;
  allowReordering?: boolean;
  className?: string;
}

interface SortableTemplateItemProps {
  item: TemplateItem;
  itemIndex: number;
  onEditClick: () => void;
  onDeleteClick: () => void;
  allowEditing: boolean;
}

interface TemplateItemComponentProps {
  item: TemplateItem;
  itemIndex: number;
  onEditClick: () => void;
  onDeleteClick: () => void;
  allowEditing: boolean;
  isDragging?: boolean;
  dragHandleProps?: any;
}

function SortableTemplateItem({
  item,
  itemIndex,
  onEditClick,
  onDeleteClick,
  allowEditing,
}: SortableTemplateItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `item-${itemIndex}` });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      <TemplateItemComponent
        item={item}
        itemIndex={itemIndex}
        onEditClick={onEditClick}
        onDeleteClick={onDeleteClick}
        allowEditing={allowEditing}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

function TemplateItemComponent({
  item,
  itemIndex,
  onEditClick,
  onDeleteClick,
  allowEditing,
  isDragging,
  dragHandleProps,
}: TemplateItemComponentProps) {
  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'hard': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div
      className={cn(
        'group flex items-center gap-3 p-3 rounded-lg border transition-all duration-200',
        'hover:border-gray-400',
        isDragging && 'opacity-50 transform rotate-1 shadow-lg z-50'
      )}
    >
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="space-y-1">
          {/* Priority/Index */}
          <span className="text-xs text-gray-400">#{itemIndex + 1}</span>

          <div className="font-medium text-sm">
            {item.content}
          </div>

          {/* Description */}
          {item.description && (
            <p className="text-xs text-gray-600">{item.description}</p>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {/* Estimated time */}
            {item.estimatedMinutes && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {item.estimatedMinutes}m
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Badges */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Difficulty Badge */}
        {item.difficulty && (
          <Badge variant="outline" className={cn('text-xs px-1 py-0', getDifficultyColor(item.difficulty))}>
            {item.difficulty}
          </Badge>
        )}
      </div>

      {/* Right side actions: Edit, Delete, Drag */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Edit Button */}
        {allowEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEditClick();
            }}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
        )}

        {/* Delete Button */}
        {allowEditing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick();
            }}
            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}

        {/* Drag Handle */}
        {dragHandleProps && (
          <div
            {...dragHandleProps}
            className="h-10 w-10 cursor-grab active:cursor-grabbing flex items-center justify-center rounded-md hover:bg-muted active:bg-blue-100 active:scale-95 transition-all duration-150 touch-none select-none"
            style={{ minWidth: '44px', minHeight: '44px' }}
          >
            <GripVertical className="h-5 w-5 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );
}

export function TemplateItemList({
  templateId,
  items,
  allowEditing = true,
  allowReordering = true,
  className,
}: TemplateItemListProps) {
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  const { reorderTemplateItems, deleteTemplateItem } = useTemplatesStore();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = parseInt(String(active.id).replace('item-', ''));
      const newIndex = parseInt(String(over.id).replace('item-', ''));

      // Create reordered items array
      const reorderedItems = arrayMove([...items], oldIndex, newIndex);

      try {
        await reorderTemplateItems(templateId, reorderedItems);
        console.log('✅ Template items reordered');
      } catch (error) {
        console.error('Failed to reorder template items:', error);
      }
    }
  };

  const handleEditClick = (index: number) => {
    setEditingItemIndex(index);
    setShowEditForm(true);
  };

  const handleEditFormClose = () => {
    setEditingItemIndex(null);
    setShowEditForm(false);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmIndex === null) return;

    try {
      await deleteTemplateItem(templateId, deleteConfirmIndex);
      setDeleteConfirmIndex(null);
      console.log('✅ Template item deleted');
    } catch (error) {
      console.error('Failed to delete template item:', error);
    }
  };

  if (items.length === 0) {
    return (
      <div className={cn('text-center py-8 text-gray-500', className)}>
        No items yet. Add items above to build your template.
      </div>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Items Count */}
      <div className="text-sm text-gray-600">
        <span className="font-medium">{items.length}</span> item{items.length !== 1 ? 's' : ''} in this template
      </div>

      {/* Items List */}
      {allowReordering ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={items.map((_, index) => `item-${index}`)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {items.map((item, index) => (
                <SortableTemplateItem
                  key={`item-${index}`}
                  item={item}
                  itemIndex={index}
                  onEditClick={() => handleEditClick(index)}
                  onDeleteClick={() => setDeleteConfirmIndex(index)}
                  allowEditing={allowEditing}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-2">
          {items.map((item, index) => (
            <TemplateItemComponent
              key={`item-${index}`}
              item={item}
              itemIndex={index}
              onEditClick={() => handleEditClick(index)}
              onDeleteClick={() => setDeleteConfirmIndex(index)}
              allowEditing={allowEditing}
            />
          ))}
        </div>
      )}

      {/* Edit Form Modal */}
      {editingItemIndex !== null && items[editingItemIndex] && (
        <TemplateItemEditForm
          isOpen={showEditForm}
          onClose={handleEditFormClose}
          templateId={templateId}
          itemIndex={editingItemIndex}
          item={items[editingItemIndex]}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirmIndex !== null}
        onClose={() => setDeleteConfirmIndex(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Template Item"
        itemName={deleteConfirmIndex !== null && items[deleteConfirmIndex] ? items[deleteConfirmIndex].content : ''}
        itemType="template item"
        description="This item will be permanently removed from the template."
      />
    </div>
  );
}
